import { useEffect, useMemo, useState } from 'react';
import { fetchMediaPage, type MediaItem } from '../media/mediaGalleryApi';
import { pushProfilePhotosPath } from '../shellHistory';

type Props = { userId: string; onVisibleTotalChange?: (total: number) => void };

export function RecentPhotosPreview({ userId, onVisibleTotalChange }: Props) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [visibleTotal, setVisibleTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void fetchMediaPage('user_profile', 'me')
      .then((page) => {
        if (cancelled) return;
        const visible = page.data
          .filter((item) => item.moderation_status !== 'rejected')
          .slice(0, 4);
        const total = page.meta.visible_total ?? 0;

        setItems(visible);
        setVisibleTotal(total);
        onVisibleTotalChange?.(total);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setVisibleTotal(0);
          onVisibleTotalChange?.(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [onVisibleTotalChange, userId]);

  const remaining = useMemo(
    () => Math.max(0, visibleTotal - items.length),
    [items.length, visibleTotal],
  );

  return (
    <section className="profile-reference-card profile-recent-photos">
      <div className="profile-reference-card__heading">
        <h2>Недавние фото</h2>
        <button type="button" onClick={pushProfilePhotosPath}>Смотреть все</button>
      </div>

      {loading ? (
        <div className="profile-photo-grid is-loading" aria-hidden="true">
          {Array.from({ length: 4 }, (_, index) => <span key={index} />)}
        </div>
      ) : items.length === 0 ? (
        <p className="profile-inline-state">Фотографии появятся после ваших визитов.</p>
      ) : (
        <div className="profile-photo-grid">
          {items.map((item, index) => (
            <button type="button" key={item.id} onClick={pushProfilePhotosPath} aria-label="Открыть галерею">
              <img src={item.thumbnail_url ?? item.url} alt="Фото из профиля" />
              {index === items.length - 1 && remaining > 0 && (
                <span className="profile-photo-more">+{remaining}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
